import { useMemo } from 'react';
import { forceVisible } from 'react-lazyload';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectTrialMode } from 'app/store/AppUI/AppUI.selectors';
import { selectNewDesignEnabled } from 'app/store/pagesToolbar/pagesToolbar.selectors';
import { selectCurrentAccount } from 'app/store/Accounts/Accounts.selectors';
import { selectPresenterTags } from 'app/store/Presenter/Presenter.selectors';
import { selectFilter } from 'app/store/page/page.selectors';
// Mui
import { Checkbox, Box } from '@mui/material';
// Components
import TagPicker from 'app/components/TagPicker';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
// Constants
import { NO_TAG_LABEL } from 'app/App.constants';
import { PagesToolbarActions } from 'app/store/pagesToolbar/pagesToolbar.slice';

const TagsPicker = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const trialMode = useAppSelector(selectTrialMode);
  const newDesignEnabled = useAppSelector(selectNewDesignEnabled);
  const filter = useAppSelector(selectFilter);
  const currentAccount:any = useAppSelector(selectCurrentAccount);
  const presenterTags = useAppSelector(selectPresenterTags);

  const { tags } = filter;

  const currentTags = useMemo(() => {
    if ( !currentAccount || !currentAccount.tags ) return presenterTags;
    return currentAccount.tags;
  }, [currentAccount, presenterTags]);

  const handleClick = (nextTag:string | null) => {
    const idx = filter.tags.indexOf(nextTag);
    const nextTags = idx !== -1 ? filter.tags.filter((_:any, i:number) => i !== idx) : [...filter.tags, nextTag];
    dispatch(PageActions.setFilter('tags', nextTags));
    dispatch(PagesToolbarActions.clearSelectedPages());
    forceVisible();
  }

  const handleChange = (newValue:unknown) => {
    const value = newValue as string[];
    dispatch(PageActions.setFilter('tags', value));
    dispatch(PagesToolbarActions.clearSelectedPages());
    forceVisible();
  }

  const options = [
    { value: null, label: NO_TAG_LABEL },
    ...currentTags.map((tag:string) => ({ value: tag, label: tag }))
  ];

  if ( trialMode ) return null;
  if ( newDesignEnabled ) return (
    <Autocomplete
      sx={{
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          minWidth: 'unset !important',
        }
      }}
      value={tags}
      onChange={handleChange}
      options={options}
      TextFieldProps={{
        label: 'Tags',
        size: 'small',
        margin: 'none'
      }}
      ListboxProps={{
        sx: {
          // Reduce option horizontal padding
          '& .MuiAutocomplete-option': {
            px: 2
          }
        }
      }}
      multiple
      limitTags={2}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.value}>
          <Checkbox sx={{ p: 1, pr: 3 }} checked={tags.includes(option.value as string)} size="small" />
          {option.label}
        </Box>
      )}
      ChipProps={{
        sx: { maxWidth: `100px !important` },
        size: 'small'
      }}
      disableCloseOnSelect
    />
  );
  return (
    <TagPicker
      id="Workspace tag filter"
      activeTags={tags}
      noTagVisible={true}
      onClick={handleClick}
    />
  )
}

export default TagsPicker;
